if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';

        // custom dimensions state
        this.outOfRangeLength = false;
        this.outOfRangeWidth = false;
        this.customPriceCalc = false;
        this.allowLength = false;
        this.allowWidth = false;
        // Re-entrancy guard: the old app had no equivalent for the custom-price
        // path, so a fast double click (or a slow network response) fired the
        // whole create-variant + cart/add flow twice in parallel - the direct
        // cause of the empty-cart / duplicate-variant bug. This flag is the fix.
        this.isSubmittingCustomPrice = false;
        this.previewDebounceTimer = null;
        // Guards against a slow/stale calculate-price response landing after a
        // newer one and clobbering the UI (e.g. showing a price, then a late
        // response from an earlier keystroke overwrites/hides it).
        this.pricePreviewRequestId = 0;

        this.initCustomDimensions();

        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
      }

      // custom dimensions setup
      initCustomDimensions() {
        const productDataEl = document.getElementById('product-dimensions-data');
        const variantDataEl = document.getElementById('variant-dimensions-data');
        this.variantBlockTopWrapper = document.querySelector('product-info');

        // if show_custom_dimensions is not enabled, un-hide the variant block and stop the related JS run!
        if (!productDataEl || !variantDataEl) {
          this.variantBlockTopWrapper.classList.add('variant-block');
          return;
        }

        this.productData = JSON.parse(productDataEl.textContent || '{}');
        this.variantData = JSON.parse(variantDataEl.textContent || '{}');

        this.lengthField = document.getElementById('dimension-length');
        this.widthField = document.getElementById('dimension-width');
        this.lengthInput = document.getElementById('custom-length-input');
        this.widthInput = document.getElementById('custom-width-input');
        this.lengthWarning = document.getElementById('length-warning');
        this.widthWarning = document.getElementById('width-warning');
        this.lengthHelp = document.getElementById('length-help');
        this.widthHelp = document.getElementById('width-help');
        this.propLength = document.getElementById('property-custom-length');
        this.propWidth = document.getElementById('property-custom-width');
        this.dynamicCheckoutBtn = document.querySelector('.shopify-payment-button');

        // New: price preview + inline error elements (see product-custom-dimensions.liquid)
        this.pricePreview = document.getElementById('custom-price-preview');
        this.pricePreviewWrapper = document.getElementById('custom-price-preview-wrapper');
        this.customError = document.getElementById('custom-dimensions-error');

        this.lengthInput?.addEventListener('input', this.handleDimensionInput.bind(this));
        this.widthInput?.addEventListener('input', this.handleDimensionInput.bind(this));

        document.querySelectorAll('input[name="id"]').forEach((r) => r.addEventListener('change', () => this.refreshDimensionUI()));
        // Real options (color/width-class) can now coexist with custom sizing (Day 6) -
        // refresh on Dawn's own variantChange event instead of a fixed setTimeout guess.
        subscribe(PUB_SUB_EVENTS.variantChange, () => this.refreshDimensionUI());

        this.refreshDimensionUI();
      }

      toNum(v) {
        const n = parseFloat(v);
        return isNaN(n) ? null : n;
      }

      getSelectedVariantTitle() {
        const selectedVariantScript = document.querySelector('script[data-selected-variant]');
        if (!selectedVariantScript) {
          return '';
        }
        const selectedVariant = JSON.parse(selectedVariantScript.textContent.trim());
        let variantTitle = selectedVariant.title;
        return variantTitle === 'Default Title' ? '' : variantTitle;
      }

      currentVariantId() {
        const sel = this.form.querySelector('select[name="id"]');
        if (sel) return sel.value;
        const radio = this.form.querySelector('input[name="id"]:checked');
        if (radio) return radio.value;
        const hidden = this.form.querySelector('input[type="hidden"][name="id"]');
        return hidden ? hidden.value : null;
      }

      // Config injected by product-custom-dimensions.liquid. Direct calls to the
      // Vercel deployment (App Proxy turned out not to be practically reachable for
      // this store - see ARCHITECTURE.md) protected by a shared secret instead.
      appBaseUrl() {
        return window.customVariantApp?.baseUrl || '';
      }

      appSharedSecret() {
        return window.customVariantApp?.sharedSecret || '';
      }

      currentProductId() {
        const hidden = this.form.querySelector('[name="product-id"]');
        return hidden ? hidden.value : null;
      }

      // getting metafields on product/variant level
      resolvedSettings(variantId) {
        const v = (this.variantData.variants && this.variantData.variants[variantId]) || {};
        const p = this.productData.product || {};

        const pick = (key, unit = false) => {
          if (key == 'allow_width' || key == 'allow_length') {
            return v[key] !== null && v[key] !== undefined && v[key] !== '' ? v[key] : p[key];
          } else {
            if (v[key] !== null && v[key] !== undefined && v[key] !== '') {
              if (unit) {
                return v[key]['unit'];
              } else {
                return v[key]['value'];
              }
            } else {
              if (p[key] !== null && p[key] !== undefined && p[key] !== '') {
                if (unit) {
                  return p[key]['unit'];
                } else {
                  return p[key]['value'];
                }
              } else {
                return '';
              }
            }
          }
        };

        return {
          allow_width: pick('allow_width'),
          allow_length: pick('allow_length'),
          width_min: this.toNum(pick('width_min')),
          width_max: this.toNum(pick('width_max')),
          width_min_unit: pick('width_min', true),
          width_max_unit: pick('width_max', true),
          length_min: this.toNum(pick('length_min')),
          length_max: this.toNum(pick('length_max')),
          length_min_unit: pick('length_min', true),
          length_max_unit: pick('length_max', true)
        };
      }

      // UI for products with custom dimensions and non~
      refreshDimensionUI() {
        const s = this.resolvedSettings(this.currentVariantId());

        if (!s.length_min) {
          s.length_min = 0;
        }
        if (!s.width_min) {
          s.width_min = 0;
        }

        this.customPriceCalc = s.allow_length === 'Yes' || s.allow_width === 'Yes';

        // Always shown now - the picker snippet excludes our internal option by
        // name (Day 6), so it's always safe to display real options alongside
        // custom sizing, regardless of temp-variant count.
        this.variantBlockTopWrapper.classList.add('variant-block');

        // allowLength/allowWidth must be reset on every refresh, not just set
        // to true - otherwise switching from a variant that allows custom
        // length to one that doesn't left the flag stuck true from before.
        this.allowLength = s.allow_length === 'Yes';
        if (this.allowLength) {
          this.lengthField.style.display = '';
          const info = [];
          if (s.length_min !== null) info.push('Min: ' + s.length_min + (s.length_min_unit === '' ? 'mm' : s.length_min_unit));
          if (s.length_max !== null) info.push('Max: ' + s.length_max + (s.length_max_unit === '' ? 'mm' : s.length_max_unit));
          this.lengthHelp.textContent = info.join(' • ');
        } else {
          this.lengthField.style.display = 'none';
          this.lengthInput.value = '';
          this.propLength.value = '';
          this.lengthWarning.style.display = 'none';
          this.outOfRangeWidth = false;
        }

        this.allowWidth = s.allow_width === 'Yes';
        if (this.allowWidth) {
          this.widthField.style.display = '';
          const info = [];
          if (s.width_min !== null) info.push('Min: ' + s.width_min + (s.width_min_unit === '' ? 'mm' : s.width_min_unit));
          if (s.width_max !== null) info.push('Max: ' + s.width_max + (s.width_max_unit === '' ? 'mm' : s.width_max_unit));
          this.widthHelp.textContent = info.join(' • ');
        } else {
          this.widthField.style.display = 'none';
          this.widthInput.value = '';
          this.propWidth.value = '';
          this.widthWarning.style.display = 'none';
          this.outOfRangeLength = false;
        }

        // Bug fix: previously this unconditionally disabled the submit button
        // whenever the newly-selected variant allows custom sizing, and never
        // re-checked it - so a customer who already typed a valid size, then
        // switched to a different real option (e.g. a color/width class),
        // was stuck with a disabled "Add to cart" until they edited the
        // custom field again. Re-validating whatever is already in the
        // visible input(s) against this variant's min/max fixes it: a
        // still-valid value re-enables the button immediately, and a value
        // that's now out of range for the new variant correctly stays
        // disabled with a warning shown, instead of either extreme.
        if (this.customPriceCalc) {
          const l = this.allowLength
            ? this.validate(this.lengthInput, this.lengthWarning, s.length_min, s.length_max, 'Länge')
            : null;
          const w = this.allowWidth
            ? this.validate(this.widthInput, this.widthWarning, s.width_min, s.width_max, 'Breite')
            : null;

          if (this.allowLength) this.propLength.value = l ?? '';
          if (this.allowWidth) this.propWidth.value = w ?? '';

          if (l == null && w == null) {
            this.disableSubmitBtns();
          }

          // Same bug, same fix, for the price preview: previously this whole
          // function ended by unconditionally hiding it, so a customer who'd
          // already entered a valid length, then switched a real option (e.g.
          // width class), watched the price disappear even though the value
          // was still sitting in the field and still valid. queuePricePreview
          // recalculates from the current values and hides only if they're
          // genuinely not ready/valid, instead of always hiding.
          this.queuePricePreview({ allowLength: this.allowLength, allowWidth: this.allowWidth, l, w });
        } else {
          console.debug('[price-preview] hiding: refreshDimensionUI ran (customPriceCalc false)');
          this.hidePricePreview();
        }
      }

      validate(input, warningEl, min, max, label) {
        if (!min) {
          min = 0;
        }

        const raw = input.value.trim();

        if (!raw) {
          warningEl.style.display = 'none';

          if (label == 'Länge') {
            this.outOfRangeWidth = false;
          } else {
            this.outOfRangeLength = false;
          }

          return null;
        }

        const val = this.toNum(raw);

        if (val === null) {
          warningEl.textContent = `${label} must be a number`;
          warningEl.style.display = '';
          return null;
        }
        if ((min !== null && val < min) || (max !== null && val > max)) {
          warningEl.textContent = `${label} ist außerhalb des gültigen Bereichs (${min ?? '1'} - ${max ?? '∞'}).`;
          warningEl.style.display = '';

          this.disableSubmitBtns();

          if (label == 'Länge') {
            this.outOfRangeWidth = true;
          } else {
            this.outOfRangeLength = true;
          }
        } else {
          warningEl.style.display = 'none';

          if (label == 'Länge') {
            this.outOfRangeWidth = false;
          } else {
            this.outOfRangeLength = false;
          }

          if (!this.outOfRangeWidth && !this.outOfRangeLength) {
            this.enableSubmitBtns();
          }
        }
        return val;
      }

      // setup line item property value + trigger a debounced price preview
      handleDimensionInput() {
        const s = this.resolvedSettings(this.currentVariantId());
        const l = this.validate(this.lengthInput, this.lengthWarning, s.length_min, s.length_max, 'Länge');
        const w = this.validate(this.widthInput, this.widthWarning, s.width_min, s.width_max, 'Breite');

        if (l == null && w == null) {
          this.disableSubmitBtns();
        }

        if (s.allow_length == 'Yes') this.propLength.value = l ?? '';
        if (s.allow_width == 'Yes') this.propWidth.value = w ?? '';

        this.queuePricePreview({ allowLength: s.allow_length == 'Yes', allowWidth: s.allow_width == 'Yes', l, w });
      }

      // --- live price preview -------------------------------------------------
      // New in this rebuild. The old app never showed a price before "Add to
      // cart", so customers only found out the (often wrong) price after the
      // fact. Debounced so we don't hit the API on every keystroke.
      queuePricePreview({ allowLength, allowWidth, l, w }) {
        clearTimeout(this.previewDebounceTimer);

        const ready = (allowLength ? l != null : true) && (allowWidth ? w != null : true) && (l != null || w != null);
        if (!this.pricePreview) return;

        if (!ready || this.outOfRangeLength || this.outOfRangeWidth) {
          console.debug('[price-preview] hiding: queuePricePreview not-ready', { ready, outOfRangeLength: this.outOfRangeLength, outOfRangeWidth: this.outOfRangeWidth, l, w });
          this.hidePricePreview();
          return;
        }

        this.previewDebounceTimer = setTimeout(() => this.fetchPricePreview({ allowLength, allowWidth, l, w }), 350);
      }

      async fetchPricePreview({ allowLength, allowWidth, l, w }) {
        const mode = allowLength && allowWidth ? 'area' : allowWidth ? 'width' : 'length';
        const productId = 'gid://shopify/Product/' + this.currentProductId();
        const selectedVariantId = this.currentVariantId();

        const requestId = ++this.pricePreviewRequestId;
        const isStale = () => requestId !== this.pricePreviewRequestId;

        this.pricePreview.textContent = window.customPriceStrings?.calculating || 'Berechne Preis...';
        if (this.pricePreviewWrapper) this.pricePreviewWrapper.hidden = false;

        try {
          const resp = await fetch(this.appBaseUrl() + '/api/calculate-price', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'X-App-Secret': this.appSharedSecret() },
            body: JSON.stringify({
              productId,
              selectedVariantId,
              mode,
              lengthMm: allowLength ? l : null,
              widthMm: allowWidth ? w : null
            })
          });

          if (isStale()) return; // a newer keystroke already superseded this request

          if (!resp.ok) {
            console.debug('[price-preview] hiding: fetch not ok', resp.status);
            this.hidePricePreview();
            return;
          }

          const json = await resp.json();
          if (isStale()) return;

          if (typeof json.price === 'number') {
            this.pricePreview.textContent = this.formatMoney(json.price);
            if (this.pricePreviewWrapper) this.pricePreviewWrapper.hidden = false;
          } else {
            console.debug('[price-preview] hiding: response had no numeric price', json);
            this.hidePricePreview();
          }
        } catch (err) {
          if (isStale()) return;
          console.error('[price-preview] hiding: fetch threw', err);
          this.hidePricePreview();
        }
      }

      hidePricePreview() {
        if (this.pricePreviewWrapper) this.pricePreviewWrapper.hidden = true;
        if (!this.pricePreview) return;
        this.pricePreview.textContent = '';
      }

      // Uses Shopify.formatMoney if the theme has it (Dawn does); falls back to
      // a plain EUR string so we never render a double currency symbol - that
      // bug in the old app was a store currency-format setting, not code, but
      // we still only ever set textContent once here, never concatenate.
      formatMoney(amountEur) {
        const cents = Math.round(amountEur * 100);
        if (typeof window.Shopify?.formatMoney === 'function' && window.theme?.moneyFormat) {
          return window.Shopify.formatMoney(cents, window.theme.moneyFormat);
        }
        return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(amountEur);
      }

      // --- inline error helper (replaces alert() on the custom-price path) ---
      showCustomError(message) {
        if (!this.customError) {
          alert(message);
          return;
        }
        this.customError.textContent = message;
        this.customError.hidden = false;
      }

      hideCustomError() {
        if (!this.customError) return;
        this.customError.hidden = true;
        this.customError.textContent = '';
      }

      enableSubmitBtns() {
        this.submitButton.disabled = false;
        if (this.dynamicCheckoutBtn) {
          this.dynamicCheckoutBtn.disabled = false;
        }
      }

      disableSubmitBtns() {
        this.submitButton.disabled = true;
        if (this.dynamicCheckoutBtn) {
          this.dynamicCheckoutBtn.disabled = true;
        }
      }

      // submit handler
      async onSubmitHandler(evt) {
        evt.preventDefault();

        // custom price flow
        if (this.customPriceCalc) {
          // Re-entrancy guard - the fix for the double-submit / duplicate /
          // empty-cart bug. Old code had no check here at all.
          if (this.isSubmittingCustomPrice) return;
          this.isSubmittingCustomPrice = true;

          this.hideCustomError();

          const allowL = this.allowLength;
          const allowW = this.allowWidth;
          const lengthMm = allowL ? Number(this.lengthInput.value) : null;
          const widthMm = allowW ? Number(this.widthInput.value) : null;

          const missing = [];
          if (allowL && (!lengthMm || Number.isNaN(lengthMm))) missing.push('Länge');
          if (allowW && (!widthMm || Number.isNaN(widthMm))) missing.push('Breite');
          if (missing.length) {
            this.showCustomError(`Bitte gültige ${missing.join(' und ')} eingeben.`);
            this.isSubmittingCustomPrice = false;
            return;
          }

          this.submitButton.setAttribute('aria-disabled', true);
          this.submitButton.disabled = true;
          this.submitButton.classList.add('loading');
          this.querySelector('.loading__spinner')?.classList.remove('hidden');

          try {
            const formData = new FormData(this.form);
            const quantity = Number(formData.get('quantity')) || 1;
            const productId = 'gid://shopify/Product/' + formData.get('product-id');
            const selectedVariantId = formData.get('id');
            const selectedVariantTitle = this.getSelectedVariantTitle();

            const mode = allowL && allowW ? 'area' : allowW ? 'width' : 'length';

            // 1. create / get the variant for this exact size (idempotent server-side -
            //    a retry after a failure below will safely reuse it, not duplicate it).
            const createResp = await fetch(this.appBaseUrl() + '/api/create-variant', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'X-App-Secret': this.appSharedSecret() },
              body: JSON.stringify({
                productId,
                selectedVariantId,
                selectedVariantTitle,
                mode,
                lengthMm: lengthMm ?? null,
                widthMm: widthMm ?? null
              })
            });

            const createJson = await createResp.json().catch(() => ({}));

            if (!createResp.ok || !createJson.success) {
              throw new Error(createJson.message || createJson.error || 'Variant creation failed');
            }

            // 2. add to cart - and actually check whether it worked this time.
            //    The old app awaited this fetch and redirected unconditionally,
            //    so a failed add (e.g. a 4xx from cart/add.js) still landed the
            //    customer on an "empty" cart page. Fixed here.
            const props = { _starter_variant_id: selectedVariantId };
            if (allowL) props['_Individuelle_Länge'] = `${lengthMm} mm`;
            if (allowW) props['_Individuelle_Breite'] = `${widthMm} mm`;

            // Shopify's storefront cart layer needs some time to catch up after
            // a variant is FIRST created via the Admin API. Testing shows this
            // delay is NOT a fixed interval - one test needed ~4.2s, another
            // needed longer than 5s - so instead of guessing a tight budget,
            // we wait an upfront chunk (only for a brand-new variant; a REUSED
            // one has already had time to settle) and then retry with a
            // generous, evenly-spaced budget as the real safety net. Better for
            // the customer to wait a few extra seconds behind the loading
            // spinner than to see a false "sold out".
            const isBrandNewVariant = !createJson.isExisting;
            if (isBrandNewVariant) {
              await new Promise((resolve) => setTimeout(resolve, 3000));
            }

            const maxCartAttempts = isBrandNewVariant ? 8 : 2;
            const retryDelayMs = 1500;
            let addResp;
            let addJson;
            for (let attempt = 1; attempt <= maxCartAttempts; attempt++) {
              addResp = await fetch('/cart/add.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  id: createJson.variantId,
                  quantity,
                  properties: props
                })
              });

              addJson = await addResp.json().catch(() => ({}));

              if (addResp.ok && !addJson.status) break;

              const isTransientSoldOut =
                addJson.status === 422 && /ausverkauft|sold out/i.test(addJson.description || addJson.message || '');

              if (!isTransientSoldOut || attempt === maxCartAttempts) {
                throw new Error(addJson.description || addJson.message || 'Konnte nicht zum Warenkorb hinzugefügt werden.');
              }

              await new Promise((resolve) => setTimeout(resolve, retryDelayMs));
            }

            window.location.href = '/cart';
          } catch (err) {
            console.error(err);
            this.showCustomError(
              err.message === 'Failed to fetch'
                ? 'Verbindung fehlgeschlagen. Bitte versuchen Sie es erneut.'
                : err.message || 'Hinzufügen zum Warenkorb fehlgeschlagen.'
            );
            this.submitButton.classList.remove('loading');
            this.submitButton.removeAttribute('aria-disabled');
            this.submitButton.disabled = false;
            this.querySelector('.loading__spinner')?.classList.add('hidden');
          } finally {
            this.isSubmittingCustomPrice = false;
          }

          return;
        }

        // default shopify flow (unchanged - not implicated in any of the reported bugs)
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner')?.classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure('add:paint-updated-sections', () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure('add:paint-updated-sections', () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner')?.classList.add('hidden');

            CartPerformance.measureFromEvent('add:user-action', evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
